import { kSourceLrrMetadataAgent } from '@/constants/constants-api-paths'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { nanoid } from 'nanoid'
import { getAuth } from 'firebase/auth'
import { kErrorCodeAuthError } from '@/constants/constants-error-codes'
import * as Sentry from '@sentry/browser'
import { ApiError } from '@/util/errors/api-error'
import { LrrReferenceMetadataResponseSchema, LrrReferenceMetadataResponse } from '../schema/lrr-reference-viewer.schema'

/**
 * Get LRR metadata for the standalone reference viewer.
 *
 * @param s3Path - The s3 directory path from reference metadata
 * @returns The LRR reference metadata (name, citation, jurisdiction, etc.)
 */
export async function getLrrMetadataForStandaloneViewer(s3Path: string): Promise<LrrReferenceMetadataResponse> {
  const sentry_transaction_id = nanoid()
  const token = await getAuth().currentUser?.getIdToken()
  if (!token) throw Error(kErrorCodeAuthError)

  try {
    const apiUrl = kPaxtonAppApiRESTBaseUrl() + kSourceLrrMetadataAgent + '/' + s3Path

    const requestOptions: RequestInit = {
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
        'X-Transaction-Id': sentry_transaction_id,
        Authorization: `Bearer ${token}`,
      },
      method: 'GET',
    }

    const response = await fetch(apiUrl, requestOptions)

    if (!response.ok) {
      let errorDetail = response.statusText
      try {
        const errorData = await response.json()
        errorDetail = JSON.stringify(errorData.detail || errorData)
      } catch {
        // Response is not JSON (e.g., HTML error page), use status text
      }

      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentry_transaction_id })

        Sentry.captureException(new Error(`LRR metadata fetch error - status: ${response.status}, detail: ${errorDetail}`), {
          extra: {
            requestUrl: apiUrl,
            responseStatus: response.status,
            responseStatusText: response.statusText,
            onLine: navigator.onLine,
            cookieEnabled: navigator.cookieEnabled,
          },
        })
      })
      throw new ApiError(response.status, `LRR metadata fetch error - status: ${response.status}, detail: ${errorDetail}`)
    }

    const data = await response.json()
    const validatedData = LrrReferenceMetadataResponseSchema.parse(data)

    return validatedData
  } catch (error) {
    if (!(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentry_transaction_id })
        Sentry.captureException(error, {
          extra: { onLine: navigator.onLine, cookieEnabled: navigator.cookieEnabled, s3Path },
        })
      })
    }
    throw error
  }
}
